import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import StorefrontLayout from "../components/storefront/Layout";
import Breadcrumbs, { breadcrumbJsonLd } from "../components/storefront/Breadcrumbs";
import { api, money } from "../lib/api";
import { useCart } from "../lib/cart";
import { resolveUnitPrice, hasVolumePricing, tierRangeLabel } from "../lib/pricing";
import FitmentAssistButton from "../components/storefront/FitmentAssistButton";
import { getActiveVehicle, computeConfidence, CONFIDENCE_META } from "../lib/fitment";
import { useSeo } from "../lib/seo";

const PART_BY_CATEGORY = { "side-mirror": "Side Mirror", windshield: "Windshield", backglass: "Backglass" };
import { Check, ChevronLeft } from "lucide-react";
import { imgSrcSet, thumbnailUrl } from "../lib/imgSrcSet";
import SafeImage from "../components/storefront/SafeImage";
import { ShippingReturnsCard, PaymentTrust } from "../components/storefront/TrustBadges";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [override, setOverride] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);
  const { add, setOpen } = useCart();
  const [activeVehicle] = useState(() => getActiveVehicle());
  const fitConfidence = useMemo(
    () => computeConfidence(product?.fitments, activeVehicle),
    [product, activeVehicle]
  );

  useEffect(() => {
    setProduct(null);
    setErrorMsg("");
    api.get(`/products/${slug}`)
      .then(async ({ data }) => {
        setProduct(data.product);
        try {
          const r = await api.get(`/seo/page?path=${encodeURIComponent("/product/" + data.product.slug)}`);
          setOverride(r.data);
        } catch { setOverride(null); }
      })
      .catch(() => setErrorMsg("Product not found."));
  }, [slug]);

  // SEO: title / description / canonical / Product+Offer schema / breadcrumb
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const primaryFitment = product?.fitments?.[0];
  // SKU stays in JSON-LD (machine-readable) but NOT in the visible title.
  const computedTitle = product ? `${product.name} | Lecis Auto Glass` : "";
  // Description may contain HTML from the rich-text editor — strip tags for
  // any text-only consumer (meta description, JSON-LD description, OG).
  const stripHtml = (s) => (s || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  const plainDesc = product ? stripHtml(product.description) : "";
  const computedDesc = product
    ? (plainDesc.slice(0, 155) || `${product.name} for sale at Lecis Auto Glass. Ships nationwide.`)
    : "";
  const path = product ? `/product/${product.slug}` : "/";
  const categoryLabel = product
    ? ({ "side-mirror": "Side Mirrors", windshield: "Windshields", backglass: "Backglass", sunroof: "Sunroof" }[product.category] || "Parts")
    : "";
  const crumbs = product ? [
    { label: "Home", to: "/" },
    { label: categoryLabel, to: `/shop/${product.category}` },
    ...(primaryFitment ? [
      { label: primaryFitment.make, to: `/${primaryFitment.make_slug}` },
      { label: primaryFitment.model, to: `/${primaryFitment.make_slug}/${primaryFitment.model_slug}` },
    ] : []),
    { label: product.name },
  ] : [];

  const productSchema = product ? {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    sku: (product.sku || "").trim(),
    mpn: (product.sku || "").trim(),
    image: (product.images || []).map((u) => /^https?:\/\//.test(u) ? u : origin + u),
    description: plainDesc,
    brand: { "@type": "Brand", name: "Lecis Auto Glass" },
    category: ({ "side-mirror": "Auto Parts > Mirrors > Side Mirrors", windshield: "Auto Parts > Glass > Windshields", backglass: "Auto Parts > Glass > Backglass", sunroof: "Auto Parts > Glass > Sunroof Glass" }[product.category]) || "Auto Parts > Glass > Windshields",
    offers: {
      "@type": "Offer",
      url: origin + path,
      priceCurrency: "USD",
      price: String(product.price),
      availability: product.inventory_qty > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
      seller: { "@type": "Organization", name: "Lecis Auto Glass" },
    },
    ...(product.fitments?.length ? {
      isAccessoryOrSparePartFor: product.fitments.slice(0, 25).map((f) => ({
        "@type": "Vehicle",
        name: `${f.year} ${f.make} ${f.model}${f.trim ? " " + f.trim : ""}`,
      })),
    } : {}),
  } : null;

  useSeo({
    title: override?.title || computedTitle,
    description: override?.meta_description || computedDesc,
    canonicalPath: override?.canonical_url || path,
    robots: override?.is_indexable === false ? "noindex,follow" : "index,follow",
    og: { image: override?.og_image || product?.images?.[0], type: "product" },
    jsonLd: {
      product: productSchema,
      breadcrumb: product ? breadcrumbJsonLd(crumbs) : null,
      seoPage: override?.schema_jsonld || null,
    },
  });

  if (errorMsg) {
    return (
      <StorefrontLayout>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-32 text-center" data-testid="product-error">
          <h1 className="font-display text-4xl mb-3">{errorMsg}</h1>
          <Link to="/shop" className="text-red-500 hover:text-red-400">Back to shop</Link>
        </div>
      </StorefrontLayout>
    );
  }

  if (!product) {
    return (
      <StorefrontLayout>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-32 text-zinc-500" data-testid="product-loading">Loading…</div>
      </StorefrontLayout>
    );
  }

  const img = product.images?.[active] || product.images?.[0];

  const featureList = [
    { key: "heated", label: "Heated" },
    { key: "blind_spot", label: "Blind spot detection" },
    { key: "auto_dimming", label: "Auto-dimming" },
    { key: "with_camera", label: "With camera" },
    { key: "hud", label: "HUD — Heads Up Display" },
    { key: "rain_sensor", label: "Rain sensor" },
    { key: "with_backing_plate", label: "w/BackingPlate" },
  ].filter((f) => product.features?.[f.key]);

  return (
    <StorefrontLayout>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <Breadcrumbs items={crumbs} />
        <Link to="/shop" className="text-xs text-zinc-500 hover:text-white inline-flex items-center gap-2 mb-8">
          <ChevronLeft size={14} /> Back to shop
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <div data-testid="product-gallery">
            <div className="aspect-[4/3] bg-white border border-zinc-200/70 rounded-md overflow-hidden mb-4 flex items-center justify-center">
              <SafeImage
                {...imgSrcSet(img, { sizes: "(max-width: 1024px) 100vw, 50vw" })}
                alt={product.name}
                className="w-full h-full object-contain p-4"
                fallbackLabel={product.name}
              />
            </div>
            {product.images?.length > 1 && (
              <div className="flex gap-3">
                {product.images.map((src, i) => (
                  <button key={i} onClick={() => setActive(i)} className={`w-20 h-20 rounded overflow-hidden border ${active === i ? "border-red-500" : "border-zinc-200/70"} bg-white flex items-center justify-center`}>
                    <img src={thumbnailUrl(src)} alt="" className="w-full h-full object-contain p-1" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div data-testid="product-info">
            {product.oem_number && (
              <div data-testid="product-oem" className="text-[10px] uppercase tracking-[0.3em] text-red-500 font-bold mb-3 font-mono">
                OEM / Equiv. {product.oem_number}
              </div>
            )}
            <h1 className="font-display text-3xl md:text-5xl tracking-tighter font-medium mb-4">{product.name}</h1>
            {activeVehicle && fitConfidence && (
              <div className="mb-4" data-testid="pdp-fit-chip">
                <span className={`inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-sm border ${CONFIDENCE_META[fitConfidence].cls}`}>
                  {fitConfidence === "likely" ? "≈ Likely fits your" : "✓ Fits your"} {activeVehicle.label}
                </span>
              </div>
            )}
            {(() => {
              const tiers = product.price_tiers || [];
              const unit = resolveUnitPrice(product.price, tiers, qty);
              const showVolume = hasVolumePricing(product.price, tiers);
              return (
                <>
                  <div className="text-3xl font-mono mb-2" data-testid="product-price">
                    {money(unit)}
                    {unit < Number(product.price) && (
                      <span className="ml-3 text-base text-zinc-500 line-through">{money(product.price)}</span>
                    )}
                    <span className="ml-2 text-sm text-zinc-500">/ each</span>
                  </div>
                  {showVolume && (
                    <div className="mb-6 border border-emerald-500/20 bg-emerald-500/5 rounded-md p-4" data-testid="product-volume-pricing">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-500 font-bold mb-3">Volume pricing — buy more, save more</div>
                      <div className="space-y-1.5">
                        {tiers.map((t, i) => {
                          const inRange = qty >= (t.min_qty ?? 1) && (t.max_qty == null || qty <= t.max_qty);
                          return (
                            <div key={i} data-testid={`product-tier-${i}`} className={`flex items-center justify-between text-sm font-mono ${inRange ? "text-emerald-400" : "text-zinc-400"}`}>
                              <span>Qty {tierRangeLabel(t)}{inRange ? "  ←" : ""}</span>
                              <span>{money(t.unit_price)} ea</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {!showVolume && <div className="mb-6" />}
                </>
              );
            })()}

            {product.description ? (
              <div
                data-testid="product-description"
                className="text-zinc-400 leading-relaxed mb-8 prose prose-invert prose-sm max-w-none prose-headings:text-zinc-100 prose-strong:text-zinc-100 prose-li:text-zinc-300 prose-p:text-zinc-400"
                dangerouslySetInnerHTML={{ __html: product.description }}
              />
            ) : null}

            <div className="grid grid-cols-2 gap-3 mb-8 text-sm">
              <Spec label="Brand" value="Lecis Auto Glass" />
              <Spec label="Category" value={({ "side-mirror": "Side Mirror", windshield: "Windshield", backglass: "Backglass", sunroof: "Sunroof" }[product.category]) || product.category || "—"} />
              <Spec label="Position" value={(!product.position || product.position === "n/a") ? "—" : product.position.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())} />
              <Spec label="Type" value={product.fitment_type === "aftermarket" ? "Aftermarket" : "OEM-Style"} />
              <Spec label="Stock" value={(product.inventory_qty || 0) > 0 ? `${product.inventory_qty} in stock` : "Out of stock"} />
            </div>

            {featureList.length > 0 && (
              <div className="mb-8">
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-3">Features</div>
                <ul className="space-y-2">
                  {featureList.map((f) => (
                    <li key={f.key} className="text-sm flex items-center gap-2 text-zinc-200">
                      <Check size={14} className="text-red-500" /> {f.label}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center border border-white/10 rounded-sm">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-12 hover:bg-white/5" data-testid="qty-decr">−</button>
                <span className="w-10 text-center font-mono" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="w-10 h-12 hover:bg-white/5" data-testid="qty-incr">+</button>
              </div>
              <button
                data-testid="product-add-to-cart"
                disabled={product.inventory_qty <= 0}
                onClick={() => { add(product, qty); }}
                className="flex-1 h-12 bg-red-600 hover:bg-red-700 text-white font-medium tracking-wide transition disabled:opacity-40 rounded-sm"
              >
                Add to cart
              </button>
            </div>

            <div className="mb-6" data-testid="pdp-fitment-cta">
              <FitmentAssistButton
                prefill={{
                  product_name: product.name,
                  product_id: product.id,
                  product_url: typeof window !== "undefined" ? window.location.href : undefined,
                  part_needed: PART_BY_CATEGORY[product.category] || "Other",
                  vehicle_year: activeVehicle?.year || "",
                  vehicle_make: activeVehicle?.make || "",
                  vehicle_model: activeVehicle?.model || "",
                  trim: activeVehicle?.trim || "",
                }}
              />
            </div>

            <PaymentTrust />

            <ShippingReturnsCard />

            {product.fitments?.length > 0 && (
              <div className="border-t border-white/10 pt-8">
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2">Compatible Vehicles ({product.fitments.length})</div>
                <p className="text-xs text-zinc-500 mb-4">Designed to fit the following vehicles. Vehicle names are shown for fitment/compatibility reference only.</p>
                <div className="max-h-64 overflow-y-auto space-y-1.5">
                  {product.fitments.map((f, i) => (
                    <div key={i} className="text-sm font-mono text-zinc-300 flex items-center justify-between border-b border-white/5 py-1.5">
                      <span>{f.year} {f.make} {f.model}</span>
                      <span className="text-zinc-500">{f.trim || ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </StorefrontLayout>
  );
}

function Spec({ label, value }) {
  return (
    <div className="border border-white/10 p-3 rounded-sm">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{label}</div>
      <div className="text-zinc-100">{value}</div>
    </div>
  );
}
